import { useState, useRef, useEffect } from "react";
import { useIsHotelEdition } from "@/lib/hotelEdition"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, X, Lightbulb } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import linaImage from "@assets/lina desk_1764231656647.png";

// Map of app feature keywords to their routes — Lina can reference these
const APP_LINKS: Array<{ patterns: string[]; url: string; label: string }> = [
  { patterns: ["Foto-Bibliothek", "Foto Bibliothek", "Fotobibliothek", "Fotos hochladen"], url: "/foto-bibliothek", label: "Foto-Bibliothek" },
  { patterns: ["Post-Bibliothek", "Post Bibliothek", "Postbibliothek"], url: "/foto-bibliothek", label: "Post-Bibliothek" },
  { patterns: ["Evergreen Content", "Evergreen-Content"], url: "/evergreen-content", label: "Evergreen Content" },
  { patterns: ["Industry News", "Branchen-News", "Branchennews"], url: "/industry-news", label: "Branchen-News" },
  { patterns: ["Napkin Wisdom"], url: "/napkin-wisdom", label: "Napkin Wisdom" },
  { patterns: ["Repurposing", "Repurposing Content", "Inhalte wiederverwenden"], url: "/repurposing", label: "Inhalte wiederverwenden" },
  { patterns: ["Pillar Posts", "Pillar-Posts", "Pillar Post", "Beiträge nach Themen"], url: "/pillar-posts", label: "Beiträge nach Themen" },
  { patterns: ["Brand Visuals", "Brand-Visuals"], url: "/brand-visuals", label: "Brand Visuals" },
  { patterns: ["Posting-Plan", "Posting Plan", "Veröffentlichungsplan"], url: "/veroeffentlichungsplan", label: "Veröffentlichungsplan" },
  { patterns: ["Marketing Setup", "Marketing-Setup"], url: "/marketing-setup", label: "Marketing Setup" },
  { patterns: ["A-Team Briefing", "Briefing"], url: "/a-team-briefing", label: "A-Team Briefing" },
  { patterns: ["Marketingstrategie"], url: "/marketingstrategie", label: "Marketingstrategie" },
  { patterns: ["Plattform verbinden", "Plattformen verbinden", "Social Media verbinden"], url: "/platform-connections", label: "Plattform verbinden" },
  { patterns: ["Cockpit", "Dashboard"], url: "/", label: "Cockpit" },
];

interface ChatMessage {
  id: string;
  role: "lina" | "user";
  content: string;
  timestamp: Date;
}

interface LinaChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
  companyId: string;
  userId: string;
  userName?: string;
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  );
}

/**
 * Render Lina's message content: strip markdown, make app feature names clickable.
 */
function renderLinaContent(text: string, onNavigate: (url: string) => void): JSX.Element[] {
  // Step 1: Strip markdown formatting
  let cleaned = text
    .replace(/\*\*([^*]+)\*\*/g, "$1")   // **bold** → bold
    .replace(/__([^_]+)__/g, "$1")       // __bold__ → bold
    .replace(/\*([^*]+)\*/g, "$1")       // *italic* → italic
    .replace(/_([^_]+)_/g, "$1")         // _italic_ → italic
    .replace(/^#{1,3}\s+/gm, "")         // ### headings → plain text
    .replace(/^[-*]\s+/gm, "• ");        // - list items → bullet

  // Step 2: Build regex to match app feature names (longest first to avoid partial matches)
  const allPatterns = APP_LINKS.flatMap((link) =>
    link.patterns.map((p) => ({ pattern: p, url: link.url, label: link.label }))
  ).sort((a, b) => b.pattern.length - a.pattern.length);

  const escapedPatterns = allPatterns.map((p) => p.pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const linkRegex = new RegExp(`(${escapedPatterns.join("|")})`, "gi");

  // Step 3: Split by lines, then within each line split by app links
  const lines = cleaned.split("\n");
  const elements: JSX.Element[] = [];

  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) elements.push(<br key={`br-${lineIndex}`} />);

    const parts = line.split(linkRegex);
    parts.forEach((part, partIndex) => {
      // Check if this part matches an app link
      const matchedLink = allPatterns.find(
        (p) => p.pattern.toLowerCase() === part.toLowerCase()
      );

      if (matchedLink) {
        elements.push(
          <button
            key={`${lineIndex}-${partIndex}`}
            className="text-purple-600 dark:text-purple-400 underline underline-offset-2 hover:text-purple-800 dark:hover:text-purple-300 font-medium"
            onClick={() => onNavigate(matchedLink.url)}
          >
            {matchedLink.label}
          </button>
        );
      } else {
        elements.push(<span key={`${lineIndex}-${partIndex}`}>{part}</span>);
      }
    });
  });

  return elements;
}

function ChatMessageBubble({ message, onNavigate }: { message: ChatMessage; onNavigate: (url: string) => void }) {
  const isLina = message.role === "lina";
  const formattedTime = message.timestamp.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className={`flex gap-3 mb-4 ${isLina ? "justify-start" : "justify-end"}`}>
      {isLina && (
        <Avatar className="w-8 h-8 border-2 border-purple-300 flex-shrink-0">
          <AvatarImage src={linaImage} alt="Lina" />
          <AvatarFallback className="bg-purple-100 text-purple-700 text-sm">L</AvatarFallback>
        </Avatar>
      )}
      <div className={`max-w-[80%] ${isLina ? "" : "order-first"}`}>
        <div
          className={`rounded-2xl px-4 py-2 ${
            isLina
              ? "bg-muted text-foreground rounded-tl-sm"
              : "bg-primary text-primary-foreground rounded-tr-sm"
          }`}
        >
          <p className="text-sm whitespace-pre-wrap">
            {isLina ? renderLinaContent(message.content, onNavigate) : message.content}
          </p>
        </div>
        <p className={`text-xs text-muted-foreground mt-1 ${isLina ? "text-left" : "text-right"}`}>
          {formattedTime}
        </p>
      </div>
    </div>
  );
}

export function LinaChatModal({ isOpen, onClose, initialMessage, companyId, userId, userName }: LinaChatModalProps) {
  const isHotel = useIsHotelEdition(); // Hotels: Sie-Form placeholders (Mike 2026-10-10)
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasInitialized = useRef(false);
  const [, setLocation] = useLocation();

  // Navigate to an app page and close the chat
  const handleNavigate = (url: string) => {
    onClose();
    setLocation(url);
  };

  const getGreeting = () => {
    const name = userName?.trim();
    return name 
      ? `Hallo ${name}, wie kann ich Ihnen heute helfen?`
      : "Hallo, wie kann ich Ihnen heute helfen?";
  };

  const buildHistory = (currentMessages: ChatMessage[], limit: number = 10) => {
    return currentMessages
      .filter(msg => !msg.id.startsWith('lina_greeting_') && !msg.id.startsWith('error_'))
      .slice(-limit)
      .map(msg => ({
        role: msg.role === "lina" ? "assistant" : "user",
        content: msg.content,
      }));
  };

  const sendMessageMutation = useMutation({
    mutationFn: async ({ message, history }: { message: string; history: Array<{ role: string; content: string }> }) => {
      return apiRequest("POST", "/api/lina-chat", {
        message,
        company_id: companyId,
        user_id: userId,
        session_id: sessionId,
        history,
      });
    },
    onSuccess: (data) => {
      setIsTyping(false);
      const linaMessage: ChatMessage = {
        id: `lina_${Date.now()}`,
        role: "lina",
        content: data.response,
        timestamp: new Date(data.timestamp || Date.now()),
      };
      setMessages((prev) => [...prev, linaMessage]);
    },
    onError: (error) => {
      setIsTyping(false);
      console.error("Lina chat error:", error);
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        role: "lina",
        content: "Entschuldigung, da ist etwas schiefgelaufen. Bitte versuchen Sie es noch einmal.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    },
  });

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector("[data-radix-scroll-area-viewport]");
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && !hasInitialized.current && companyId && userId) {
      hasInitialized.current = true;
      
      if (initialMessage && initialMessage.trim()) {
        const userMessage: ChatMessage = {
          id: `user_${Date.now()}`,
          role: "user",
          content: initialMessage.trim(),
          timestamp: new Date(),
        };
        setMessages([userMessage]);
        setIsTyping(true);
        sendMessageMutation.mutate({ message: initialMessage.trim(), history: [] });
      } else {
        const greetingMessage: ChatMessage = {
          id: `lina_greeting_${Date.now()}`,
          role: "lina",
          content: getGreeting(),
          timestamp: new Date(),
        };
        setMessages([greetingMessage]);
      }
    }
  }, [isOpen, initialMessage, companyId, userId]);

  useEffect(() => {
    if (!isOpen) {
      hasInitialized.current = false;
      setMessages([]);
      setInputValue("");
      setIsTyping(false);
    }
  }, [isOpen]);

  const handleSend = () => {
    const trimmedMessage = inputValue.trim();
    if (!trimmedMessage || sendMessageMutation.isPending) return;

    const history = buildHistory(messages);

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: "user",
      content: trimmedMessage,
      timestamp: new Date(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);
    sendMessageMutation.mutate({ message: trimmedMessage, history });

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0 gap-0">
        <DialogHeader className="flex flex-row items-center gap-3 p-4 border-b bg-purple-50 dark:bg-purple-950/30 rounded-t-lg">
          <Avatar className="w-10 h-10 border-2 border-purple-300">
            <AvatarImage src={linaImage} alt="Lina" />
            <AvatarFallback className="bg-purple-100 text-purple-700">L</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <DialogTitle className="text-base font-semibold">Lina</DialogTitle>
            <p className="text-xs text-muted-foreground">Ihre Marketing-Strategin</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
          {/* Teaser Box - shown at the start of conversation */}
          {messages.length <= 1 && (
            <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg mb-4 border border-purple-200 dark:border-purple-800">
              <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-4 h-4" />
              </div>
              <div>
                <h4 className="font-medium text-purple-900 dark:text-purple-100 text-sm">Ihr Weg zum Marketing-Profi</h4>
                <p className="text-xs text-purple-700 dark:text-purple-300 leading-relaxed">
                  Ich zeige Ihnen, wie Sie das Beste aus Ihrem A-Team herausholen.
                </p>
              </div>
            </div>
          )}

          {messages.map((message) => (
            <ChatMessageBubble key={message.id} message={message} onNavigate={handleNavigate} />
          ))}
          {isTyping && (
            <div className="flex gap-3 mb-4">
              <Avatar className="w-8 h-8 border-2 border-purple-300 flex-shrink-0">
                <AvatarImage src={linaImage} alt="Lina" />
                <AvatarFallback className="bg-purple-100 text-purple-700 text-sm">L</AvatarFallback>
              </Avatar>
              <div className="bg-muted rounded-2xl rounded-tl-sm">
                <TypingIndicator />
              </div>
            </div>
          )}
        </ScrollArea>

        <div className="p-4 border-t bg-background">
          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isHotel ? "Schreiben Sie Lina eine Nachricht..." : "Schreib Lina eine Nachricht..."}
              className="min-h-[44px] max-h-[120px] resize-none"
              rows={1}
              disabled={sendMessageMutation.isPending}
            />
            <Button
              onClick={handleSend}
              disabled={!inputValue.trim() || sendMessageMutation.isPending}
              className="bg-purple-600 hover:bg-purple-700 h-[44px] w-[44px] p-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Enter = Senden, Shift+Enter = Neue Zeile
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
